import axios, { AxiosError, type AxiosProgressEvent, type AxiosRequestConfig } from 'axios';
import type { ApiEnvelope, UploadResult } from '../types/api';
import { useAuthStore } from '../stores/auth';
import { useUiStore } from '../stores/ui';

declare module 'axios' {
  interface AxiosRequestConfig {
    globalLoading?: boolean;
  }

  interface InternalAxiosRequestConfig {
    globalLoading?: boolean;
  }
}

const client = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL || '/api', timeout: 15000, withCredentials: true });
client.interceptors.request.use((config) => {
  if (config.globalLoading !== false) useUiStore().begin();
  const auth = useAuthStore(); if (auth.token) config.headers.Authorization = `Bearer ${auth.token}`; return config;
});
client.interceptors.response.use((response) => {
  if (response.config.globalLoading !== false) useUiStore().end();
  const body = response.data as ApiEnvelope<unknown>;
  if (body && typeof body === 'object' && 'code' in body) {
    if (body.code !== 0) return Promise.reject(new Error(body.message || '请求失败'));
    response.data = body.data;
  }
  return response;
}, (error: AxiosError<ApiEnvelope<unknown>>) => {
  if (error.config?.globalLoading !== false) useUiStore().end();
  if (error.response?.status === 401) {
    const auth = useAuthStore(); auth.clear();
    if (!location.pathname.startsWith('/auth/')) auth.begin(location.pathname + location.search + location.hash);
  }
  const message = error.response?.data?.message || (error.code === 'ECONNABORTED' ? '请求超时，请重试' : error.message || '网络连接失败');
  useUiStore().show(message); return Promise.reject(new Error(message));
});

export const api = {
  async request<T>(config: AxiosRequestConfig) { return (await client.request<T>(config)).data; },
  async get<T>(url: string, params?: object, config?: AxiosRequestConfig) { return (await client.get<T>(url, { ...config, params })).data; },
  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) { return (await client.post<T>(url, data, config)).data; },
  async patch<T = unknown>(url: string, data?: unknown) { return (await client.patch<T>(url, data)).data; },
};
export async function uploadImage(file: File, purpose: 'AVATAR' | 'PAYMENT_QR' | 'SUBMISSION_EVIDENCE', onProgress?: (percent: number) => void) {
  if (!file.type.startsWith('image/')) throw new Error('请选择图片文件');
  if (file.size > 10 * 1024 * 1024) throw new Error('图片不能超过 10MB');
  const prepared = await compressImage(file);
  const body = new FormData(); body.append('file', prepared, prepared.name); body.append('purpose', purpose);
  return api.request<UploadResult>({ method: 'POST', url: '/upload/image', data: body, timeout: 60000,
    onUploadProgress: (event: AxiosProgressEvent) => onProgress?.(event.total ? Math.round(event.loaded * 100 / event.total) : 0) });
}
async function compressImage(file: File): Promise<File> {
  if (file.size < 1024 * 1024 || /gif|svg/i.test(file.type)) return file;
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image(); image.src = objectUrl;
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('图片读取失败')); });
    const scale = Math.min(1, 1920 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas'); canvas.width = Math.max(1, Math.round(image.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg', lastModified: file.lastModified }) : file;
  } catch { return file; } finally { URL.revokeObjectURL(objectUrl); }
}
export async function privateImage(fileId: string) {
  const response = await client.get<Blob>(`/my/files/${fileId}/content`, { responseType: 'blob' }); return URL.createObjectURL(response.data);
}
